import { SignatureUtils } from '../utils/signature';
import { ActionBuilder } from '../actions/ActionBuilder';
import { RequestSender } from './RequestSender';
import {
  ExchangeAction,
  OrderRequest,
  UserOrderRequest,
  OrderResponse,
  CancelResponse,
  TwapOrderResponse,
  TwapCancelResponse,
  DefaultResponse,
} from '../types/exchange';
import { TimeInForce, ClientOrderId } from '../types/hyperliquid/orders';

export interface HyperliquidExchangeConfig {
  vaultAddress?: string;
  builder?: {
    address: string;
    fee: number;
    cloid?: string;
  };
}

/**
 * HyperliquidExchange - Refactored version with separated concerns
 * - ActionBuilder: Creates action objects
 * - SignatureUtils: Handles signing
 * - RequestSender: Sends HTTP requests
 */
export class HyperliquidAgent {
  // private readonly signer: SignatureUtils;
  private readonly actionBuilder: ActionBuilder;
  private readonly requestSender: RequestSender;
  private readonly isMainnet: boolean;
  private readonly vaultAddress?: string;

  constructor(config: HyperliquidExchangeConfig) {
    this.isMainnet = process.env.NEXT_PUBLIC_NETWORK === 'MAINNET';
    // Initialize components
    // this.signer = new SignatureUtils(config.apiPrivateKey, this.isMainnet);
    this.actionBuilder = new ActionBuilder(config.builder);
    this.vaultAddress = config.vaultAddress;

    const baseURL = process.env.NEXT_PUBLIC_HYPERLIQUID_API_ENDPOINT;

    this.requestSender = new RequestSender({ baseURL });
  }

  /**
   * Send signed requests for L1 actions (orders, cancels, etc.)
   * Public method for custom actions
   */
  async sendSignedL1Request<T = any>(
    action: ExchangeAction,
    signer: SignatureUtils,
    expiresAfter?: number,
  ): Promise<T> {
    const nonce = Date.now();
    const signature = await signer.signL1Action(action, nonce, this.vaultAddress);
    return this.requestSender.sendExchangeRequest<T>(action, signature, nonce, this.vaultAddress, expiresAfter);
  }

  /**
   * Send signed requests for user actions (transfers, withdrawals, etc.)
   * Public method for custom actions
   */
  async sendSignedUserRequest<T = any>(
    action: ExchangeAction & { nonce?: number; time?: number },
    signer: SignatureUtils,
  ): Promise<T> {
    const nonce = action.nonce || action.time || Date.now();
    const signature = await signer.signUserSignedAction(action);
    return this.requestSender.sendExchangeRequest<T>(action, signature, nonce);
  }

  /**
   * Order Management
   */
  async placeOrder(orderRequest: UserOrderRequest, signer: SignatureUtils): Promise<OrderResponse> {
    const { asset, isBuy, price, size, reduceOnly = false, tif = 'Gtc', cloid } = orderRequest;

    const order = this.actionBuilder.createLimitOrder(asset, isBuy, price, size, reduceOnly, tif, cloid);
    return this.placeMultipleOrders([order], signer);
  }

  async placeTriggerOrder(
    asset: number,
    isBuy: boolean,
    triggerPrice: string,
    size: string,
    tpsl: 'tp' | 'sl',
    signer: SignatureUtils,
    isMarket: boolean = true,
    reduceOnly: boolean = true,
    cloid?: ClientOrderId,
  ): Promise<OrderResponse> {
    const order = this.actionBuilder.createTriggerOrder(
      asset,
      isBuy,
      triggerPrice,
      size,
      tpsl,
      isMarket,
      reduceOnly,
      cloid,
    );
    return this.placeMultipleOrders([order], signer);
  }

  async placeMultipleOrders(
    orders: OrderRequest[],
    signer: SignatureUtils,
    grouping: 'na' | 'normalTpsl' | 'positionTpsl' = 'na',
  ): Promise<OrderResponse> {
    const action = this.actionBuilder.placeOrderAction(orders, grouping);
    return this.sendSignedL1Request<OrderResponse>(action, signer);
  }

  async cancelOrder(asset: number, oid: number, signer: SignatureUtils): Promise<CancelResponse> {
    const action = this.actionBuilder.cancelOrderAction([{ asset, oid }]);
    return this.sendSignedL1Request<CancelResponse>(action, signer);
  }

  async cancelMultipleOrders(
    cancels: Array<{ asset: number; oid: number }>,
    signer: SignatureUtils,
  ): Promise<CancelResponse> {
    const action = this.actionBuilder.cancelOrderAction(cancels);
    return this.sendSignedL1Request<CancelResponse>(action, signer);
  }

  async cancelByCloid(asset: number, cloid: string, signer: SignatureUtils): Promise<CancelResponse> {
    const action = this.actionBuilder.cancelByCloidAction([{ asset, cloid }]);
    return this.sendSignedL1Request<CancelResponse>(action, signer);
  }

  async scheduleCancel(signer: SignatureUtils, time?: number): Promise<DefaultResponse> {
    const action = this.actionBuilder.scheduleCancelAction(time);
    return this.sendSignedL1Request<DefaultResponse>(action, signer);
  }

  async modifyOrder(oid: number | string, order: OrderRequest, signer: SignatureUtils): Promise<DefaultResponse> {
    const action = this.actionBuilder.modifyOrderAction(oid, order);
    return this.sendSignedL1Request<DefaultResponse>(action, signer);
  }

  async batchModifyOrders(
    modifies: Array<{ oid: number | string; order: OrderRequest }>,
    signer: SignatureUtils,
  ): Promise<DefaultResponse> {
    const action = this.actionBuilder.batchModifyAction(modifies);
    return this.sendSignedL1Request<DefaultResponse>(action, signer);
  }

  /**
   * Leverage & Margin Management
   */
  async updateLeverage(
    asset: number,
    leverage: number,
    signer: SignatureUtils,
    isCross: boolean = true,
  ): Promise<DefaultResponse> {
    const action = this.actionBuilder.updateLeverageAction(asset, leverage, isCross);
    return this.sendSignedL1Request<DefaultResponse>(action, signer);
  }

  async updateIsolatedMargin(
    asset: number,
    marginChange: number,
    signer: SignatureUtils,
    isBuy: boolean = true,
  ): Promise<DefaultResponse> {
    const action = this.actionBuilder.updateIsolatedMarginAction(asset, marginChange, isBuy);
    return this.sendSignedL1Request<DefaultResponse>(action, signer);
  }

  async topUpIsolatedMargin(asset: number, leverage: string, signer: SignatureUtils): Promise<DefaultResponse> {
    const action = this.actionBuilder.topUpIsolatedMarginAction(asset, leverage);
    return this.sendSignedL1Request<DefaultResponse>(action, signer);
  }

  /**
   * Transfer Operations
   */
  async sendUsdc(destination: string, amount: string, signer: SignatureUtils): Promise<DefaultResponse> {
    const action = this.actionBuilder.usdSendAction(destination, amount);
    return this.sendSignedUserRequest<DefaultResponse>(action, signer);
  }

  async sendSpot(destination: string, token: string, amount: string, signer: SignatureUtils): Promise<DefaultResponse> {
    const action = this.actionBuilder.spotSendAction(destination, token, amount);
    return this.sendSignedUserRequest<DefaultResponse>(action, signer);
  }

  async withdraw(destination: string, amount: string, signer: SignatureUtils): Promise<DefaultResponse> {
    const action = this.actionBuilder.withdrawAction(destination, amount);
    return this.sendSignedUserRequest<DefaultResponse>(action, signer);
  }

  async transferUsdClass(
    amount: string,
    toPerp: boolean,
    signer: SignatureUtils,
    subaccount?: string,
  ): Promise<DefaultResponse> {
    const action = this.actionBuilder.usdClassTransferAction(amount, toPerp, subaccount);
    return this.sendSignedUserRequest<DefaultResponse>(action, signer);
  }

  async sendAsset(
    destination: string,
    token: string,
    amount: string,
    signer: SignatureUtils,
    sourceDex: string = '',
    destinationDex: string = 'spot',
    fromSubAccount: string = '',
  ): Promise<DefaultResponse> {
    const action = this.actionBuilder.sendAssetAction(
      destination,
      token,
      amount,
      sourceDex,
      destinationDex,
      fromSubAccount,
    );
    return this.sendSignedUserRequest<DefaultResponse>(action, signer);
  }

  /**
   * Staking Operations
   */
  async depositToStaking(wei: number, signer: SignatureUtils): Promise<DefaultResponse> {
    const action = this.actionBuilder.stakingDepositAction(wei);
    return this.sendSignedUserRequest<DefaultResponse>(action, signer);
  }

  async withdrawFromStaking(wei: number, signer: SignatureUtils): Promise<DefaultResponse> {
    const action = this.actionBuilder.stakingWithdrawAction(wei);
    return this.sendSignedUserRequest<DefaultResponse>(action, signer);
  }

  async delegateToValidator(
    validator: string,
    wei: number,
    signer: SignatureUtils,
    isUndelegate: boolean = false,
  ): Promise<DefaultResponse> {
    const action = this.actionBuilder.tokenDelegateAction(validator, wei, isUndelegate);
    return this.sendSignedUserRequest<DefaultResponse>(action, signer);
  }

  /**
   * Vault Operations
   */
  async depositToVault(vaultAddress: string, amount: number, signer: SignatureUtils): Promise<DefaultResponse> {
    const action = this.actionBuilder.vaultTransferAction(vaultAddress, amount, true);
    return this.sendSignedL1Request<DefaultResponse>(action, signer);
  }

  async withdrawFromVault(vaultAddress: string, amount: number, signer: SignatureUtils): Promise<DefaultResponse> {
    const action = this.actionBuilder.vaultTransferAction(vaultAddress, amount, false);
    return this.sendSignedL1Request<DefaultResponse>(action, signer);
  }

  /**
   * Approval Operations
   */
  async approveAgent(agentAddress: string, signer: SignatureUtils, agentName?: string): Promise<DefaultResponse> {
    const action = this.actionBuilder.approveAgentAction(agentAddress, agentName);
    return this.sendSignedUserRequest<DefaultResponse>(action, signer);
  }

  async approveBuilderFee(builder: string, maxFeeRate: string, signer: SignatureUtils): Promise<DefaultResponse> {
    const action = this.actionBuilder.approveBuilderFeeAction(builder, maxFeeRate, Date.now());
    return this.sendSignedUserRequest<DefaultResponse>(action, signer);
  }

  /**
   * TWAP Operations
   */
  async placeTwapOrder(
    asset: number,
    isBuy: boolean,
    size: string,
    minutes: number,
    signer: SignatureUtils,
    randomize: boolean = false,
    reduceOnly: boolean = false,
  ): Promise<TwapOrderResponse> {
    const action = this.actionBuilder.twapOrderAction(asset, isBuy, size, minutes, randomize, reduceOnly);
    return this.sendSignedL1Request<TwapOrderResponse>(action, signer);
  }

  async cancelTwapOrder(asset: number, twapId: number, signer: SignatureUtils): Promise<TwapCancelResponse> {
    const action = this.actionBuilder.twapCancelAction(asset, twapId);
    return this.sendSignedL1Request<TwapCancelResponse>(action, signer);
  }

  /**
   * Utility Operations
   */
  async reserveRequestWeight(weight: number, signer: SignatureUtils): Promise<DefaultResponse> {
    const action = this.actionBuilder.reserveRequestWeightAction(weight);
    return this.sendSignedL1Request<DefaultResponse>(action, signer);
  }

  async noop(signer: SignatureUtils): Promise<DefaultResponse> {
    const action = this.actionBuilder.noopAction();
    return this.sendSignedL1Request<DefaultResponse>(action, signer);
  }

  getActionBuilder(): ActionBuilder {
    return this.actionBuilder;
  }

  getRequestSender(): RequestSender {
    return this.requestSender;
  }
}
